/**
 * @architecture-header 1
 * @layer module
 * @domain feedback
 * @responsibility Render the SH-F08 product-feedback modal opened from HelpPopover's 功能許願池
 *   element: category selector, free-text body, submit. A resend with the same dedupeKey (e.g. a
 *   double-submit) lands on the same thank-you state as a fresh submission, never an error.
 * @state-owner modal-local view/form state; no TanStack Query cache (v1 has no read-back operation)
 * @allowed-imports React; shared overlay/modal primitives; same-module hooks
 * @forbidden routes; direct API client; a client-side list of past submissions (none exists in v1)
 * @public-api FeedbackModal
 * @side-effects submits product feedback
 * @tests FeedbackModal.test.tsx and frontend/apps/web/e2e/sh-f08.spec.ts
 */

import { type FormEvent, useState } from 'react';
import { ModalDocument, OptionCard } from '../../../shared/components/Modal';
import { useTranslator } from '../../../shared/model/use-workspace';
import {
  type FeedbackCategory,
  newFeedbackDedupeKey,
  useSubmitFeedback,
} from '../api/feedback.mutations';

const CATEGORY_OPTIONS: readonly {
  value: FeedbackCategory;
  title: string;
  description: string;
}[] = [
  { value: 'bug', title: '問題回報', description: '系統出現錯誤或不如預期的狀況' },
  { value: 'improvement', title: '改善建議', description: '現有功能可以做得更好的地方' },
  { value: 'feature_idea', title: '功能許願', description: '你希望未來加入的新功能' },
];

function FeedbackForm({ onSubmitted }: { onSubmitted: () => void }) {
  const t = useTranslator();
  const mutation = useSubmitFeedback();
  const [category, setCategory] = useState<FeedbackCategory | null>(null);
  const [text, setText] = useState('');
  // ONE dedupeKey per logical submission (round-1 finding 1): created once when this draft
  // mounts, reused across every retry of it, and only replaced by a genuinely NEW draft --
  // FeedbackForm unmounting and remounting after success/reset, which re-runs this initializer.
  const [dedupeKey] = useState(() => newFeedbackDedupeKey());

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const body = text.trim();
    if (!category || !body) return;
    try {
      await mutation.mutateAsync({ category, text: body, dedupeKey });
      onSubmitted();
    } catch {
      // Drafted category and text stay put -- never silently discarded on error. dedupeKey is
      // deliberately NOT rotated here (round-1 finding 1): if the request actually committed
      // server-side but the response was lost, a retry must resend the SAME key so the server's
      // own dedupe path returns the existing row instead of inserting a duplicate. Only a genuinely
      // new draft -- FeedbackForm remounting after success/reset -- gets a fresh key, via this same
      // useState initializer running again.
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="feedback-categories">
        {CATEGORY_OPTIONS.map((option) => (
          <OptionCard
            description={t(option.description)}
            key={option.value}
            on={category === option.value}
            title={t(option.title)}
            onSelect={() => setCategory(option.value)}
          />
        ))}
      </div>
      <label className="feedback-field">
        <span>{t('詳細說明')}</span>
        <textarea
          disabled={mutation.isPending}
          maxLength={4000}
          onChange={(event) => setText(event.target.value)}
          placeholder={t('請具體描述你的意見，我們會納入產品規劃的參考')}
          rows={5}
          value={text}
        />
      </label>
      {mutation.isError ? (
        <p className="feedback-error" role="alert">
          {t('送出失敗，請稍後再試。')}
        </p>
      ) : null}
      <button
        className="bt pri feedback-submit"
        disabled={mutation.isPending || !category || !text.trim()}
        type="submit"
      >
        {mutation.isPending ? t('送出中…') : t('送出')}
      </button>
    </form>
  );
}

export function FeedbackModal() {
  const t = useTranslator();
  const [submitted, setSubmitted] = useState(false);

  return (
    <ModalDocument
      className="feedback-modal"
      dialogLabel={t('功能許願池')}
      maxWidth={480}
      title={t('功能許願池')}
    >
      {submitted ? (
        <div className="feedback-success">
          <p>{t('感謝你的意見！')}</p>
          <button
            className="bt pri feedback-submit"
            type="button"
            onClick={() => setSubmitted(false)}
          >
            {t('再提供一次意見')}
          </button>
        </div>
      ) : (
        <FeedbackForm onSubmitted={() => setSubmitted(true)} />
      )}
    </ModalDocument>
  );
}
